import { I18n } from "@assets/strings";
import { identity } from "lodash";
import React from "react";
import { useLocation } from "react-router";
import { Link } from "react-router";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import logoOmat from "@assets/images/logo_omat.png";
import { config } from "@core/config/config";
import { Environment } from "@core/config/environment";
import { BrowserLocalStorage } from "@core/localStorage/storage";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { ApplicationVoteDto } from "@domain/application/dtos";
import { useApplicationByUser } from "@domain/application/hooks";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Profile } from "@domain/user/profile";
import { Person } from "@domain/user/user";
import { useAsync, useVoteOnLocalStorage } from "@utils/hooks";
import { PrimaryButton, SecondaryTouchButton } from "@ui/components/buttons/primaryButton";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { LikeIcon, ShareIcone } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { LinkExternal } from "@ui/components/linkExternal";
import { TooshareVideoPlayer } from "@ui/components/media/tooshareVideoPlayer";
import { useModalTools } from "@ui/modals/modalContext";
import { ShareProfileModale } from "@ui/pages/application/applicationEditorPage/shareProfile";
import { theme } from "@ui/styles/theme";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";

interface ApplicationStoryCardProps extends React.HTMLAttributes<HTMLDivElement> {
	//applicationId: string;
	profile: Profile;
	voterId?: Person;
	isFeed?: boolean;
}

//TODO optimize code

// function checkDate(votedDate: Date): boolean {
// 	return !(new Date().getHours() - new Date(votedDate).getHours() >= 24);
// }

export const ApplicationStoryCard: React.FC<ApplicationStoryCardProps> = ({ profile, isFeed, ...htmlProps }) => {
	const currentProfile = useCurrentProfile();
	const { applicationService } = useServices();
	const location = useLocation();
	const { createModal } = useModalTools();

	const votesStorage = new BrowserLocalStorage<ApplicationVoteDto | null>("votes", identity, identity, "local");
	const votesOnLocalStorage = votesStorage?.getItem();

	const { result: application, loading } = useApplicationByUser(profile.id, currentProfile?.id);

	const { hasVoted, voteCount, lastVote, vote } = useVoteOnLocalStorage(
		profile.slug ?? location.pathname.slice(9).replaceAll("/", ""),
		currentProfile,
		application ?? null
	);

	if (!applicationService) {
		return null;
	}

	const { run } = useAsync(
		async (applicationId: string, key?: string): Promise<ApplicationVoteDto> => {
			vote();
			createToast(I18n.t("vote.success_toast.title"), I18n.t("vote.success_toast.message"), "success");
			const result = await applicationService.vote(applicationId, key);

			if (!votesOnLocalStorage) {
				votesStorage.setItem(result);
			} else {
				votesStorage.setItem({ ...votesOnLocalStorage, ...result });
			}

			return result;
		},
		[application?.id]
	);

	return !application ? (
		loading ? (
			<ClipLoader />
		) : null
	) : application.status === "PENDING_VALIDATION" ? (
		<Container {...htmlProps}>
			{isFeed && (
				<StyledCanonicalLink>
					<Link to={Path.Profile(profile)}>{`${Environment.APP_BASE_URL}${Path.Profile(profile)}`}</Link>
				</StyledCanonicalLink>
			)}
			<Content>
				<StyledOmatHeader>
					<StyledBlocTalent>
						<LinkExternal to="https://onemillionafricantalents.com">
							<img src={logoOmat} width="95px" height="29px" />
						</LinkExternal>
						<Link to="httonemillionafricantalents.com"></Link>
						<TalentTitle>{I18n.t("vote.baseline.label")}</TalentTitle>
					</StyledBlocTalent>
					{!isFeed && (
						<StyledBlocCounter>
							<CounterValue>
								{I18n.t(voteCount > 1 ? "vote.counter.label" : "vote.counter.single.label", {
									total: voteCount,
								})}
							</CounterValue>
						</StyledBlocCounter>
					)}
				</StyledOmatHeader>
				{!!application.videoId && <TooshareVideoPlayer asset={application.videoId} />}
				<BlocBtn>
					{!isFeed ? (
						<>
							<ShareButton
								onClick={() =>
									createModal((close) => (
										<ShareProfileModale
											canonicalLink={`${Environment.APP_BASE_URL}${Path.Profile(profile)}`}
											close={close}
											person={profile}
										/>
									))
								}
								disabled={!config.features.program}
							>
								<ShareIcone />
								<StyledLabel>{I18n.t("vote.button.share.label")}</StyledLabel>
							</ShareButton>
							<VotedButton
								onClick={() => {
									if (!currentProfile) {
										if (!lastVote) {
											run(application.id);
											return;
										}
										if (lastVote) {
											run(application.id, lastVote.key);
											return;
										}
									}
									run(application.id);
								}}
								disabled={!config.features.program}
							>
								<LikeIcon color={!hasVoted ? theme.colors.white : "#5D5D5D"} />
								<StyledLabel active={hasVoted}>{I18n.t("vote.button.vote.label")}</StyledLabel>
							</VotedButton>
						</>
					) : (
						<>
							<VotedButton
								onClick={() => {
									if (!currentProfile) {
										if (!lastVote) {
											run(application.id);
											return;
										}
										if (lastVote) {
											run(application.id, lastVote.key);
											return;
										}
									}
									run(application.id);
								}}
								disabled={!config.features.program}
							>
								<LikeIcon color={!hasVoted ? theme.colors.white : "#5D5D5D"} />
								<StyledLabel active={hasVoted}>{I18n.t("vote.button.vote.label")}</StyledLabel>
							</VotedButton>
							<CounterValue>
								{I18n.t(voteCount > 1 ? "vote.counter.label" : "vote.counter.single.label", {
									total: voteCount,
								})}
							</CounterValue>
						</>
					)}
				</BlocBtn>
			</Content>
		</Container>
	) : null;
};

const Container = styled.div`
	${gap(16)};
`;

const StyledOmatHeader = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	margin-top: 20px;
	margin-left: 20px;
	margin-right: 20px;
`;
const StyledBlocTalent = styled.div`
	display: flex;
	flex-direction: column;
`;
const StyledBlocCounter = styled.div`
	display: flex;
	flex-direction: row;
`;
const TalentTitle = styled.div`
	display: flex;
	flex-direction: column;
	font-family: Sora;
	font-style: normal;
	font-weight: 700;
	font-size: 10px;
	line-height: 25px;
	color: #206af8;
`;

const CounterValue = styled.div`
	font-family: Sora;
	font-style: normal;
	font-weight: 700;
	font-size: 20px;
	line-height: 25px;
	color: #000000;
`;

const Content = styled(RoundedBlock)`
	${gap(15)};
`;

const ShareButton = styled(SecondaryTouchButton)`
	${row(10, "space-between", "center")}
	svg {
		width: 20px;
		height: 20px;
	}
`;

const VotedButton = styled(PrimaryButton)`
	${row(10, "space-between", "center")}
	svg {
		width: 20px;
		height: 20px;
	}
`;

const BlocBtn = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	gap: 10px;
	margin: 15px 5px;
`;

const StyledLabel = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>`
	${({ theme }) => theme.textStyles.title}
	font-size: 14px !important;
	color: ${({ active, theme }) => (!active ? theme.colors.white : "#5D5D5D")} !important;
`;

const StyledCanonicalLink = styled.div`
	cursor: pointer;
	color: ${({ theme }) => theme.colors.black};
`;
